import { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import filtersStyles from "../SignRequestsFilters.styles";
import {
  EMPTY_SIGN_REQUESTS_FILTERS,
  getClearedFilter,
  getFilterValueCount,
  SignRequestsFilterKey,
  SignRequestsFilterLookups,
  SignRequestsFilters,
} from "../SignRequestsFiltersHelper";
import { CloseGlyph } from "./SignRequestsIcons";
import SignRequestsFilterPanel from "./SignRequestsFilterPanel";

const styles = filtersStyles;

const TITLE_ID = "sign-requests-filters-title";
const PANE_ID = "sign-requests-filters-pane";

interface SignRequestsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The filters currently applied — the draft starts from these. */
  filters: SignRequestsFilters;
  onApply: (filters: SignRequestsFilters) => void;
  /** The filters on offer — Sender is absent for a user who sees only their own. */
  categories: { key: SignRequestsFilterKey; label: string }[];
  lookups: SignRequestsFilterLookups;
  /** The category to open on; defaults to the first. */
  initialCategory?: SignRequestsFilterKey;
  isLoadingTags: boolean;
  isLoadingOptions: boolean;
  onManageTags: () => void;
  onCreateFirstTag: () => void;
}

/**
 * The "All filters" drawer: every filter category down a rail on the left,
 * the selected one's editor on the right. It edits a draft copy of the filters
 * and hands it back on Apply, so closing the drawer discards the changes.
 */
const SignRequestsFilterDrawer = ({
  open,
  onClose,
  filters,
  onApply,
  categories,
  lookups,
  initialCategory,
  isLoadingTags,
  isLoadingOptions,
  onManageTags,
  onCreateFirstTag,
}: SignRequestsFilterDrawerProps) => {
  const [draft, setDraft] = useState<SignRequestsFilters>(filters);
  const [activeKey, setActiveKey] = useState<SignRequestsFilterKey>("status");

  useEffect(() => {
    if (!open) return;
    setDraft(filters);
    setActiveKey(initialCategory ?? categories[0]?.key ?? "status");
    // Re-seeded on open only: the applied filters cannot change underneath an
    // open drawer, and re-seeding on every render would wipe the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (change: Partial<SignRequestsFilters>) =>
    setDraft((previous) => ({ ...previous, ...change }));

  const activeCategory =
    categories.find((category) => category.key === activeKey) ?? categories[0];
  const activeCount = categories.filter(
    (category) => getFilterValueCount(draft, category.key) > 0,
  ).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The custom-range calendar opens in a layer of its own, with its own
      // focus trap; the drawer must not pull focus back out of it.
      disableEnforceFocus
      sx={styles.drawerRoot}
      PaperProps={{
        sx: styles.drawerPaper,
        role: "dialog",
        "aria-labelledby": TITLE_ID,
      }}
    >
      <Box sx={styles.drawerHead}>
        <Box sx={styles.drawerHeadText}>
          <Typography id={TITLE_ID} component="h2" sx={styles.drawerTitle}>
            All filters
          </Typography>
          <Box component="span" sx={styles.drawerSubtitle}>
            {activeCount
              ? `${activeCount} ${
                  activeCount === 1 ? "filter" : "filters"
                } active`
              : "No filters applied"}
          </Box>
        </Box>
        <IconButton
          aria-label="Close filters"
          onClick={onClose}
          sx={styles.drawerClose}
        >
          <CloseGlyph sx={styles.drawerCloseGlyph} />
        </IconButton>
      </Box>

      <Box sx={styles.drawerBody}>
        <Box
          role="tablist"
          aria-orientation="vertical"
          aria-label="Filter categories"
          sx={styles.drawerRail}
        >
          {categories.map((category) => {
            const active = category.key === activeCategory?.key;
            return (
              <ButtonBase
                key={category.key}
                role="tab"
                aria-selected={active}
                aria-controls={PANE_ID}
                sx={styles.drawerTab(active)}
                onClick={() => setActiveKey(category.key)}
              >
                <Box component="span" sx={styles.drawerTabLabel}>
                  {category.label}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>

        {activeCategory && (
          <Box id={PANE_ID} role="tabpanel" sx={styles.drawerPane}>
            <Box sx={styles.drawerPaneHead}>
              <Typography component="h3" sx={styles.drawerPaneTitle}>
                {activeCategory.label}
              </Typography>
              {getFilterValueCount(draft, activeCategory.key) > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => patch(getClearedFilter(activeCategory.key))}
                >
                  Clear
                </Button>
              )}
            </Box>
            <Box sx={styles.drawerPaneBody}>
              <SignRequestsFilterPanel
                // Remounted per category so its search and date mode start clean.
                key={activeCategory.key}
                category={activeCategory.key}
                draft={draft}
                onChange={patch}
                lookups={lookups}
                fill
                isLoadingTags={isLoadingTags}
                isLoadingOptions={isLoadingOptions}
                onManageTags={onManageTags}
                onCreateFirstTag={onCreateFirstTag}
              />
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={styles.drawerFoot}>
        <Button
          variant="text"
          onClick={() => setDraft(EMPTY_SIGN_REQUESTS_FILTERS)}
        >
          Clear all
        </Button>
        <Button
          variant="contained"
          size="large"
          sx={styles.drawerApply}
          onClick={() => onApply(draft)}
        >
          Apply
        </Button>
      </Box>
    </Drawer>
  );
};

export default SignRequestsFilterDrawer;
